import React from 'react';
import { Card, Tag, Tooltip } from 'antd';
import {
  WarningOutlined,
  FileProtectOutlined,
  AlertOutlined,
  CheckSquareOutlined,
  LineChartOutlined,
  SafetyCertificateOutlined,
  RightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { SectionCard, EmptyBox, OPS_COLORS } from '../Operations/OperationsUI';

// ---------------------------------------------------------------------------
// Health & Safety dashboard tiles.
//
// Every tile can render real data or, when the backend reports `is_sample`,
// illustrative example values tagged with <SampleBadge/> so the user sees what
// the populated tile will look like. Shared styling comes from the Operations
// UI kit so the two dashboards read as one system.
// ---------------------------------------------------------------------------

// A visible tag marking a tile as sample data.
export const SampleBadge = () => (
  <Tooltip title="Sample data — this system has no information recorded to populate this, once records are populated this will update automatically.">
    <Tag color="gold" style={{ marginInlineEnd: 0, fontWeight: 600, cursor: 'default' }}>
      <InfoCircleOutlined style={{ marginRight: 4 }} />
      SAMPLE DATA
    </Tag>
  </Tooltip>
);

// A "View all X →" link used in tile headers / footers.
export const ViewAllLink = ({ label, onClick }) => (
  <a
    onClick={onClick}
    style={{ color: OPS_COLORS.green, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
  >
    {label} <RightOutlined style={{ fontSize: 10 }} />
  </a>
);

// SectionCard title with the sample badge sitting UNDER the title (so a long
// title is never truncated by an inline badge).
export const TileTitle = ({ title, isSample }) => (
  <div>
    <div>{title}</div>
    {isSample ? <div style={{ marginTop: 4 }}><SampleBadge /></div> : null}
  </div>
);

const formatDelta = (delta, invertGood) => {
  if (delta === null || delta === undefined) return null;
  const positive = delta >= 0;
  // For most safety metrics a rise is worse; invertGood flips that (e.g. training).
  const good = invertGood ? positive : !positive;
  const color = good ? OPS_COLORS.greenBright : OPS_COLORS.orange;
  const arrow = positive ? '▲' : '▼';
  return (
    <span style={{ color }}>
      {arrow} {Math.abs(delta)}% vs last period
    </span>
  );
};

// Headline metric card with an optional sample badge and "view all" link.
const MetricCard = ({ label, value, sub, icon, accent, isSample, footer }) => (
  <Card
    bordered={false}
    bodyStyle={{ padding: 20 }}
    style={{
      height: '100%',
      borderRadius: 10,
      borderLeft: `4px solid ${accent || 'transparent'}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ color: OPS_COLORS.grey, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <span style={{ fontSize: 18, color: '#bfbfbf' }}>{icon}</span>
    </div>
    {isSample ? <div style={{ marginTop: 6 }}><SampleBadge /></div> : null}
    <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.2, margin: '8px 0 4px' }}>{value}</div>
    {sub ? <div style={{ fontSize: 13 }}>{sub}</div> : null}
    {footer ? <div style={{ marginTop: 10 }}>{footer}</div> : null}
  </Card>
);

// Half-circle gauge for the Safety Performance score (0–100).
export const SafetyPerformanceTile = ({ data }) => {
  const score = data?.score ?? 0;
  const angle = Math.PI * (1 - score / 100);
  const cx = 90;
  const cy = 90;
  const r = 72;
  const x = cx + r * Math.cos(angle);
  const y = cy - r * Math.sin(angle);
  const color =
    score >= 80 ? OPS_COLORS.greenBright : score >= 50 ? OPS_COLORS.orange : OPS_COLORS.red;
  return (
    <Card
      bordered={false}
      bodyStyle={{ padding: 20 }}
      style={{ height: '100%', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div style={{ color: OPS_COLORS.grey, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Safety Performance (MTD)
      </div>
      {data?.is_sample ? <div style={{ marginTop: 6 }}><SampleBadge /></div> : null}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <svg width="180" height="110" viewBox="0 0 180 110">
          <path d="M18 90 A72 72 0 0 1 162 90" fill="none" stroke="#f0f0f0" strokeWidth="12" strokeLinecap="round" />
          <path
            d={`M18 90 A72 72 0 0 1 ${x} ${y}`}
            fill="none"
            stroke={color}
            strokeWidth="12"
            strokeLinecap="round"
          />
          <text x="90" y="82" textAnchor="middle" fontSize="30" fontWeight="700" fill="#262626">
            {score}
          </text>
          <text x="90" y="102" textAnchor="middle" fontSize="13" fill={color} fontWeight="600">
            {data?.label || ''}
          </text>
        </svg>
      </div>
      <div style={{ textAlign: 'center', fontSize: 13 }}>{formatDelta(data?.delta, true)}</div>
    </Card>
  );
};

// Total records with a mini sparkline bar chart.
export const TotalRecordsTile = ({ data }) => {
  const spark = (data?.sparkline || []).map((v, i) => ({ i, v }));
  return (
    <MetricCard
      label="Total Records (MTD)"
      value={(data?.count ?? 0).toLocaleString()}
      sub={formatDelta(data?.delta)}
      icon={<FileProtectOutlined />}
      accent={OPS_COLORS.green}
      isSample={data?.is_sample}
      footer={
        <div style={{ height: 40 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={spark}>
              <Bar dataKey="v" fill={OPS_COLORS.green} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      }
    />
  );
};

export const IncidentsTile = ({ data, onViewAll }) => (
  <MetricCard
    label="Incidents (MTD)"
    value={data?.count ?? 0}
    sub={formatDelta(data?.delta)}
    icon={<AlertOutlined />}
    accent={OPS_COLORS.red}
    isSample={data?.is_sample}
    footer={<ViewAllLink label="View all incidents" onClick={onViewAll} />}
  />
);

export const OpenActionsTile = ({ data, onViewAll }) => {
  const row = (color, label, count) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, margin: '2px 0' }}>
      <span>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: color, marginRight: 8 }} />
        {label}
      </span>
      <span style={{ fontWeight: 600 }}>{count}</span>
    </div>
  );
  return (
    <Card
      bordered={false}
      bodyStyle={{ padding: 20 }}
      style={{ height: '100%', borderRadius: 10, borderLeft: `4px solid ${OPS_COLORS.orange}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ color: OPS_COLORS.grey, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          Open Actions
        </div>
        <CheckSquareOutlined style={{ color: '#bfbfbf' }} />
      </div>
      {data?.is_sample ? <div style={{ marginTop: 6 }}><SampleBadge /></div> : null}
      <div style={{ fontSize: 30, fontWeight: 700, margin: '8px 0 8px' }}>{data?.total ?? 0}</div>
      {row(OPS_COLORS.red, 'Overdue', data?.overdue ?? 0)}
      {row(OPS_COLORS.orange, 'Due this week', data?.due_this_week ?? 0)}
      {row(OPS_COLORS.grey, 'Due later', data?.due_later ?? 0)}
      <div style={{ marginTop: 10 }}>
        <ViewAllLink label="View all actions" onClick={onViewAll} />
      </div>
    </Card>
  );
};

export const NearMissRatioTile = ({ data }) => {
  const trend = (data?.trend || []).map((v, i) => ({ i, v }));
  return (
    <MetricCard
      label="Near Miss Ratio (MTD)"
      value={`${data?.ratio ?? 0}%`}
      sub={formatDelta(data?.delta, true)}
      icon={<LineChartOutlined />}
      isSample={data?.is_sample}
      footer={
        <div style={{ height: 40 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend}>
              <Line type="monotone" dataKey="v" stroke={OPS_COLORS.grey} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      }
    />
  );
};

export const TrainingComplianceTile = ({ data, onViewAll }) => {
  const pct = data?.percentage ?? 0;
  return (
    <MetricCard
      label="Training Compliance"
      value={`${pct}%`}
      sub={formatDelta(data?.delta, true)}
      icon={<SafetyCertificateOutlined />}
      accent={OPS_COLORS.greenBright}
      isSample={data?.is_sample}
      footer={
        <>
          <div style={{ height: 8, borderRadius: 4, background: '#f0f0f0', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: OPS_COLORS.greenBright }} />
          </div>
          <ViewAllLink label="View training" onClick={onViewAll} />
        </>
      }
    />
  );
};

// 5×5 likelihood × consequence risk matrix.
export const RiskHeatMapTile = ({ data, onViewAll }) => {
  const matrix = data?.matrix || [];
  const consequence = data?.consequence || [];
  const likelihood = data?.likelihood || [];
  // Colour band from the cell's risk position (row+col): green → amber → red.
  const cellColor = (r, c) => {
    const risk = (matrix.length - 1 - r) + c; // higher likelihood + consequence = hotter
    const max = matrix.length + consequence.length - 2;
    const t = max ? risk / max : 0;
    if (t < 0.33) return '#d9f7be';
    if (t < 0.5) return '#fff1b8';
    if (t < 0.7) return '#ffd591';
    return '#ffa39e';
  };
  return (
    <SectionCard
      fill
      title={<TileTitle title="Risk Heat Map" isSample={data?.is_sample} />}
      extra={<ViewAllLink label="View all hazards" onClick={onViewAll} />}
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 4, width: '100%' }}>
          <thead>
            <tr>
              <th />
              {consequence.map((c) => (
                <th key={c} style={{ fontSize: 11, color: OPS_COLORS.grey, fontWeight: 500, padding: '0 2px' }}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((rowArr, r) => (
              <tr key={r}>
                <td style={{ fontSize: 11, color: OPS_COLORS.grey, whiteSpace: 'nowrap', paddingRight: 6 }}>
                  {likelihood[r]}
                </td>
                {rowArr.map((val, c) => (
                  <td
                    key={c}
                    style={{
                      background: val ? cellColor(r, c) : '#fafafa',
                      textAlign: 'center',
                      borderRadius: 4,
                      height: 30,
                      minWidth: 34,
                      fontSize: 13,
                      fontWeight: val ? 600 : 400,
                      color: val ? '#262626' : '#d9d9d9',
                    }}
                  >
                    {val || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: OPS_COLORS.grey, marginTop: 6 }}>
        Consequence / Likelihood
      </div>
    </SectionCard>
  );
};

export const IncidentsOverTimeTile = ({ data, onViewAll }) => {
  const series = data?.series || [];
  return (
    <SectionCard
      fill
      title={<TileTitle title="Incidents Over Time" isSample={data?.is_sample} />}
      extra={<ViewAllLink label="View incidents" onClick={onViewAll} />}
    >
      <div style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <RTooltip />
            <Legend />
            <Line type="monotone" name="This year" dataKey="this_year" stroke={OPS_COLORS.red} strokeWidth={2} dot={false} />
            <Line type="monotone" name="Prior year" dataKey="prior_year" stroke="#bfbfbf" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
};

export const TopHazardCategoriesTile = ({ data, onViewAll }) => {
  const categories = data?.categories || [];
  const max = Math.max(1, ...categories.map((c) => c.count));
  const palette = [OPS_COLORS.red, OPS_COLORS.orange, '#faad14', OPS_COLORS.greenBright, OPS_COLORS.green];
  return (
    <SectionCard
      fill
      title={<TileTitle title="Top Hazard Categories" isSample={data?.is_sample} />}
      extra={<ViewAllLink label="View all hazards" onClick={onViewAll} />}
    >
      {categories.length === 0 ? (
        <EmptyBox text="No hazards recorded" />
      ) : (
        categories.map((cat, i) => (
          <div key={cat.label} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span>{cat.label}</span>
              <span style={{ fontWeight: 600 }}>{cat.count}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: '#f0f0f0', overflow: 'hidden' }}>
              <div style={{ width: `${(cat.count / max) * 100}%`, height: '100%', background: palette[i % palette.length] }} />
            </div>
          </div>
        ))
      )}
    </SectionCard>
  );
};

export const ObservationsStatusTile = ({ data, onViewAll }) => {
  const breakdown = data?.breakdown || [];
  const colors = [OPS_COLORS.greenBright, '#faad14', OPS_COLORS.red];
  return (
    <SectionCard
      fill
      title={<TileTitle title="Observations Status" isSample={data?.is_sample} />}
      extra={<ViewAllLink label="View all observations" onClick={onViewAll} />}
    >
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={breakdown}
              dataKey="count"
              nameKey="label"
              cx="50%"
              cy="45%"
              innerRadius={50}
              outerRadius={72}
              paddingAngle={2}
            >
              {breakdown.map((entry, i) => (
                <Cell key={entry.label} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <RTooltip />
            <Legend verticalAlign="bottom" height={28} iconType="circle" />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ textAlign: 'center', fontSize: 13, color: OPS_COLORS.grey, marginTop: 4 }}>
        {data?.total ?? 0} total observations
      </div>
    </SectionCard>
  );
};
